import { getAppErrorHandler, getErrorBoundaryErrorHandler, getExceptionErrorHandler, getDebugInfoMessageHandler } from 'lib/sentry'

export const ERROR_TYPE = {
    NETWORK: 'network',
    WEBPACK_JS_CHUNK: 'webpack_js_chunk',
    WEBPACK_CSS_CHUNK: 'webpack_css_chunk',
    INTERNAL: 'internal',
    MY_NETTIX: 'omanettix',
    TRAFI: 'trafi',
    PGW: 'pgw',
}

export const ERROR_DISPLAY_METHOD = {
    PAGE: 'page',
    HEADER: 'header',
    NOTIFY: 'notify',
}

/**
 * Class representing an application error.
 *
 * This class is used to standardize error handling across the application.
 * It extracts and formats error messages, associates error codes, and
 * provides methods to handle different types of errors, including network
 * errors, internal errors, and specific application errors.
 *
 * The class also includes static methods to read errors from various sources
 * such as Apollo errors and exceptions, and to check the type of the error.
 */
class AppError {
    type
    code
    message
    displayMethod
    data
    errorLogId

    /**
     * Create an AppError.
     * @param {string} type - The type of the error.
     * @param {number} code - The error code.
     * @param {string} message - The error message.
     * @param {boolean} [displayMethod=false] - The method to display the error.
     * @param {Object} [data=false] - Additional data related to the error.
     * @param {number} [errorLogId=false] - The error log ID.
     */
    constructor (type, code, message, displayMethod = false, data = false, errorLogId = false) {
        const { cleanedMessage, extractedErrorLogId } = this.extractErrorLogIdAndMessage(message)
        this.type = type
        this.code = code
        this.message = cleanedMessage
        this.displayMethod = displayMethod
        this.data = data
        this.errorLogId = errorLogId || extractedErrorLogId
    }

    /**
     * Extract the error log ID and cleaned message from the original message.
     * @param {string} message - The original error message.
     * @returns {Object} An object containing the cleaned message and extracted error log ID.
     */
    extractErrorLogIdAndMessage (message) {
        if (typeof message !== 'string') {
            return { cleanedMessage: '', extractedErrorLogId: null }
        }
        const match = message.match(/\|(\d+)/)
        if (match) {
            return {
                cleanedMessage: message.replace(match[0], '').trim(),
                extractedErrorLogId: match[1]
            }
        }
        return { cleanedMessage: message, extractedErrorLogId: null }
    }

    static isOmanettixType = (appError, code = null) => {
        return appError.type === ERROR_TYPE.MY_NETTIX && (!code || appError.code === code)
    }

    static readFromApolloError (error) {
        if (!error) return ERRORS.UNKNOWN_ERROR

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            error = error.graphQLErrors[0]
        } else if (error.networkError) {
            error = error.networkError
        }

        if (!error) return ERRORS.UNKNOWN_ERROR

        if (error.statusCode) {
            switch (error.statusCode) {
            case 401:
                return ERRORS.NETWORK_ERROR_401
            case 403:
                return ERRORS.NETWORK_ERROR_403
            }
        }

        // check if we have error already in expected object format
        if (error.type && error.code) {
            return new AppError(error.type, error.code, error.message || null, false, error.data || null, error.errorLogId || null)
        }

        const message = error.toString()

        if (message.startsWith('MyNettixException:')) {
            return AppError.readFromAppException(error)
        }

        if (message.indexOf('TypeError: Failed to fetch') !== -1) {
            return ERRORS.NETWORK_ERROR
        }

        // For some reason 403 Forbidden is not being recognized as networkError
        // thus we need to check the message
        if (message.indexOf('Unauthorized access') !== -1) {
            return new AppError(
                ERROR_TYPE.NETWORK,
                403,
                message
            )
        }

        // Problem loading chunk that doesn't exist anymore.
        // This can occur when user with old app version attempts to access chunk that no longer exists.
        // Hash in chunk name changes when the chunk contents changes.
        if (/^ChunkLoadError: Loading chunk (.*) failed(.*)/.test(message)) {
            return new AppError(
                ERROR_TYPE.WEBPACK_JS_CHUNK,
                10099,
                message,
                false
            )
        } else if (/^Error: Loading CSS chunk (.*) failed(.*)/.test(message)) {
            return new AppError(
                ERROR_TYPE.WEBPACK_CSS_CHUNK,
                10100,
                message,
                false
            )
        }

        return new AppError(
            ERROR_TYPE.INTERNAL,
            1000,
            message,
            ERROR_DISPLAY_METHOD.HEADER
        )
    }

    static readFromException () {
        return ERRORS.UNKNOWN_ERROR
    }

    /**
     * If error message starts with "MyNettixException:" it should contain json with fields: type, code, message
     * @param error Apollo error
     * @returns {AppError}
     */
    static readFromAppException (error) {
        const data = JSON.parse(error.substring(error.indexOf('{')))
        return new AppError(data.type, data.code, data.message, false, data.data, data.errorLogId)
    }
}

export const ERROR_STYLE = Object.freeze({
    ERROR: 'error',
    INFO: 'info',
})

export const ERRORS = Object.freeze({
    // INTERNAL
    UNKNOWN_ERROR: new AppError(
        ERROR_TYPE.INTERNAL,
        1000,
        'Unexpected error'
    ),
    NETWORK_ERROR: new AppError(
        ERROR_TYPE.INTERNAL,
        1001,
        'Failed to fetch',
        ERROR_DISPLAY_METHOD.NOTIFY
    ),
    GENERIC_TRAFI_VEHICLE_RESTRICTION: new AppError(
        ERROR_TYPE.INTERNAL,
        1002
    ),
    SESSION_EXPIRED: new AppError(
        ERROR_TYPE.INTERNAL,
        1003,
        'Session expired',
        false,
        false
    ),
    USER_FAILED_TO_LOGIN: new AppError(
        ERROR_TYPE.INTERNAL,
        1004,
        'User failed to login'
    ),
    INACTIVE_USER: new AppError(
        ERROR_TYPE.INTERNAL,
        1005,
        'Inactive user'
    ),
    REQUIRE_LOGOUT: new AppError(
        ERROR_TYPE.INTERNAL,
        1006,
        'Require logout'
    ),
    BOS_ROLE_SWITCH: new AppError(
        ERROR_TYPE.INTERNAL,
        1007,
        'Bos role switch'
    ),
    BOS_REQUIRE_LOGOUT: new AppError(
        ERROR_TYPE.INTERNAL,
        1008,
        'Bos require logout'
    ),
    OFFER_REQUIRE_LOGOUT: new AppError(
        ERROR_TYPE.INTERNAL,
        1009,
        'Offer require logout'
    ),
    ACTIVE_VEHICLES_LIMIT: new AppError(
        ERROR_TYPE.INTERNAL,
        1010,
        'Active vehicle limit'
    ),

    // NETWORK
    NETWORK_ERROR_401: new AppError(
        ERROR_TYPE.NETWORK,
        401,
        'Unauthorized'
    ),
    NETWORK_ERROR_403: new AppError(
        ERROR_TYPE.NETWORK,
        403,
        'Access forbidden'
    ),
})

export function getGenericError (message) {
    return new AppError(
        ERROR_TYPE.INTERNAL,
        1000,
        message || 'Unexpected error'
    )
}

export function assert (condition, message = 'Assert condition failed') {
    if (!condition) {
        logAssert(message)
    }
}

export function logAppError (error, appError) {
    return getAppErrorHandler()(error, appError)
}

export function logException (error) {
    return getExceptionErrorHandler()(error)
}

export function logExceptionDebug (error) {
    return getExceptionErrorHandler('Debug')(error)
}

export function logAssert (error) {
    return getExceptionErrorHandler('Assert', console.warn)(error)
}

export function logErrorBoundaryError (error, info) {
    return getErrorBoundaryErrorHandler()(error, info)
}

export function logDebugInfoMessage (message) {
    return getDebugInfoMessageHandler()(message)
}

export default AppError
